import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
import { useTalentMatchingTabContext } from './talent_matching_tab_context';

type IndexingStatusContextValue = {
  indexingInProgress: boolean;
};

const IndexingStatusContext = createContext<IndexingStatusContextValue>({
  indexingInProgress: false,
});

type ProviderProps = {
  pollOnLoad: boolean;
  interval?: number;
  children: React.ReactNode;
};

export const IndexingStatusProvider = ({
  pollOnLoad,
  interval = 5000,
  children,
}: ProviderProps) => {
  const { paths } = useTalentMatchingTabContext();
  const [pollingEnabled, setPollingEnabled] = useState(pollOnLoad);

  const query = useQuery({
    queryKey: ['indexingStatus'],
    queryFn: async () => {
      const { data } = await standardRequestWithErrorFlash.get(
        paths.indexingStatusPath
      );

      return data.indexing_in_progress;
    },
    enabled: pollingEnabled,
    refetchInterval: pollingEnabled ? interval : false,
    staleTime: 0,
  });

  useEffect(() => {
    if (query.data !== undefined && !query.data) {
      setPollingEnabled(false);
    }
  }, [query.data]);

  return (
    <IndexingStatusContext.Provider
      value={{ indexingInProgress: !!query.data }}
    >
      {children}
    </IndexingStatusContext.Provider>
  );
};

export const useIndexingStatus = () => useContext(IndexingStatusContext);
